import React, { useState } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../service/firebase";
import bcrypt from "bcryptjs";
import "./SellerDeleteAccountPlayStore.css";

const SellerDeleteAccountPlayStore = () => {
  const [step, setStep] = useState(1); // 1: login, 2: verification, 3: confirmation
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sellerInfo, setSellerInfo] = useState(null);

  // Step 1: Login with mobile and password
  const handleLogin = async () => {
    if (!mobile || mobile.length !== 10 || isNaN(mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Find seller by mobile number
      const q = query(collection(db, "sellers"), where("mobile", "==", mobile));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No JMI Seller account found with this mobile number");
        setLoading(false);
        return;
      }

      // Get the first matching seller (should be only one)
      const sellerDoc = querySnapshot.docs[0];
      const sellerData = sellerDoc.data();

      // Verify password
      const isPasswordValid = await bcrypt.compare(password, sellerData.passwordHash);
      if (!isPasswordValid) {
        setError("Invalid password. Please try again.");
        setLoading(false);
        return;
      }

      // Store seller info and proceed to verification step
      setSellerInfo({
        sellerId: sellerDoc.id,
        mobile: sellerData.mobile,
        businessName: sellerData.businessName || 'Not specified',
        createdAt: sellerData.createdAt
      });
      setStep(2);
      setError("");
    } catch (err) {
      console.error("Login error:", err);
      setError("Failed to verify account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Final confirmation and deletion
  const deleteAccountPermanently = async () => {
    if (confirmText !== "DELETE MY ACCOUNT") {
      setError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (!sellerInfo) {
      setError("Account information not found. Please start over.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const batch = writeBatch(db);
      const { sellerId, mobile } = sellerInfo;

      // Verify account still exists before deletion
      const sellerRef = doc(db, "sellers", sellerId);
      const sellerSnap = await getDoc(sellerRef);
      
      if (!sellerSnap.exists()) {
        setError("Account not found. It may have been already deleted.");
        setLoading(false);
        return;
      }

      // 1. Delete seller's main document
      batch.delete(sellerRef);

      // 2. Delete seller's products
      const productsQuery = query(
        collection(db, "products"),
        where("sellerId", "==", sellerId)
      );
      const productsSnapshot = await getDocs(productsQuery);
      productsSnapshot.forEach((productDoc) => {
        batch.delete(productDoc.ref);
      });

      // 3. Delete seller's orders
      const ordersQuery = query(
        collection(db, "orders"),
        where("sellerId", "==", sellerId)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      ordersSnapshot.forEach((orderDoc) => {
        batch.delete(orderDoc.ref);
      });

      // 4. Delete seller's inventory records
      try {
        const inventoryQuery = query(
          collection(db, "inventory"),
          where("sellerId", "==", sellerId)
        );
        const inventorySnapshot = await getDocs(inventoryQuery);
        inventorySnapshot.forEach((inventoryDoc) => {
          batch.delete(inventoryDoc.ref);
        });
      } catch (err) {
        console.log("No inventory records to delete");
      }

      // 5. Delete seller's business information
      try {
        const businessQuery = query(
          collection(db, "business"),
          where("sellerId", "==", sellerId)
        );
        const businessSnapshot = await getDocs(businessQuery);
        businessSnapshot.forEach((businessDoc) => {
          batch.delete(businessDoc.ref);
        });
      } catch (err) {
        console.log("No business records to delete");
      }

      // 6. Delete any OTPs associated with this mobile
      const otpQuery = query(
        collection(db, "otps"),
        where("mobile", "==", `+91${mobile}`)
      );
      const otpSnapshot = await getDocs(otpQuery);
      otpSnapshot.forEach((otpDoc) => {
        batch.delete(otpDoc.ref);
      });

      // 7. Delete seller's notifications if any
      try {
        const notificationsQuery = query(
          collection(db, "notifications"),
          where("sellerId", "==", sellerId)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        notificationsSnapshot.forEach((notifDoc) => {
          batch.delete(notifDoc.ref);
        });
      } catch (err) {
        console.log("No notifications to delete");
      }

      // 8. Delete seller's analytics data if any
      try {
        const analyticsQuery = query(
          collection(db, "analytics"),
          where("sellerId", "==", sellerId)
        );
        const analyticsSnapshot = await getDocs(analyticsQuery);
        analyticsSnapshot.forEach((analyticsDoc) => {
          batch.delete(analyticsDoc.ref);
        });
      } catch (err) {
        console.log("No analytics data to delete");
      }

      // Execute all deletions
      await batch.commit();

      // Show success and reset
      setStep(3);
      setError("");
      
    } catch (err) {
      console.error("Account deletion error:", err);
      setError("Failed to delete account. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  // Reset form to start over
  const resetForm = () => {
    setStep(1);
    setMobile("");
    setPassword("");
    setConfirmText("");
    setSellerInfo(null);
    setError("");
  };

  return (
    <div className="seller-delete-wrapper">
      <div className="seller-delete-container">
        <header className="seller-app-header">
          <h1 className="seller-app-name">JMI Seller</h1>
          <p className="seller-app-tagline">Account Deletion Request</p>
        </header>

        {/* Data Deletion Information */}
        <section className="seller-data-deletion-info">
          <h2>📱 JMI Seller Account Deletion Process</h2>
          <p>
            To request deletion of your JMI Seller account and associated data, 
            please follow the steps below. This process is permanent and irreversible.
          </p>

          <div className="seller-data-types">
            <div className="seller-data-section">
              <h3>✅ Data That Will Be Deleted Immediately</h3>
              <ul>
                <li>Your personal profile information (name, mobile number, email)</li>
                <li>Business information and store details</li>
                <li>All your product listings and inventory data</li>
                <li>Order history and transaction records</li>
                <li>Business documents and verification data</li>
                <li>Payment and bank account information</li>
                <li>Notification preferences and settings</li>
                <li>OTP verification records</li>
                <li>Sales analytics and performance data</li>
                <li>All other JMI Seller account-associated data</li>
              </ul>
            </div>

            <div className="seller-data-section">
              <h3>⏳ Data Retention & Legal Compliance</h3>
              <ul>
                <li><strong>Immediate Deletion:</strong> Most personal and business data is deleted immediately upon account deletion request</li>
                <li><strong>30-Day Backup Period:</strong> Some data may remain in secure backups for up to 30 days for security purposes</li>
                <li><strong>Legal Requirements:</strong> Financial transaction records may be retained as required by Indian laws and regulations</li>
                <li><strong>Business Continuity:</strong> Completed order data may be retained for customer service purposes</li>
                <li><strong>Fraud Prevention:</strong> Minimal data may be retained to prevent fraud and maintain platform integrity</li>
              </ul>
            </div>

            <div className="seller-data-section">
              <h3>📋 Steps to Delete Your Seller Account</h3>
              <ol>
                <li>Enter your registered mobile number and password</li>
                <li>Verify your account ownership</li>
                <li>Confirm deletion by typing "DELETE MY ACCOUNT"</li>
                <li>Receive confirmation of account deletion</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Account Deletion Form */}
        <section className="seller-deletion-form-section">
          <h2>🔐 Request JMI Seller Account Deletion</h2>
          
          {step === 1 && (
            <div className="seller-login-step">
              <h3>Step 1: Verify Your JMI Seller Account</h3>
              <p>Enter your registered mobile number and password to continue with account deletion.</p>
              
              <div className="seller-form-group">
                <label htmlFor="sellerMobile">Registered Mobile Number</label>
                <input
                  id="sellerMobile"
                  type="tel"
                  placeholder="Enter your 10-digit mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  maxLength="10"
                  disabled={loading}
                />
              </div>

              <div className="seller-form-group">
                <label htmlFor="sellerPassword">Account Password</label>
                <input
                  id="sellerPassword"
                  type="password"
                  placeholder="Enter your JMI Seller account password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleLogin();
                  }}
                />
              </div>

              {error && <div className="seller-error-message">{error}</div>}

              <button
                className="seller-verify-btn"
                onClick={handleLogin}
                disabled={loading || !mobile || mobile.length !== 10 || !password}
              >
                {loading ? "Verifying JMI Seller Account..." : "Verify Account"}
              </button>
            </div>
          )}

          {step === 2 && sellerInfo && (
            <div className="seller-confirmation-step">
              <h3>Step 2: Final Confirmation</h3>
              <div className="seller-account-summary">
                <p><strong>JMI Seller Account:</strong> +91 {sellerInfo.mobile}</p>
                <p><strong>Seller ID:</strong> {sellerInfo.sellerId}</p>
                <p><strong>Business Name:</strong> {sellerInfo.businessName}</p>
                <p><strong>Account Created:</strong> {sellerInfo.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}</p>
              </div>
              
              <div className="seller-final-warning">
                <h4>⚠️ Final Warning - Permanent Action</h4>
                <p>
                  You are about to <strong>permanently delete</strong> your JMI Seller account. 
                  This action <strong>cannot be undone or reversed</strong>. All your data including 
                  products, orders, business information, and analytics will be permanently erased.
                </p>
                <p style={{marginTop: '15px', fontWeight: 'bold'}}>
                  ⚠️ All your product listings will be removed and will no longer be available for purchase.
                </p>
              </div>

              <div className="seller-form-group">
                <label htmlFor="sellerConfirmText">
                  To confirm account deletion, please type <strong>"DELETE MY ACCOUNT"</strong> exactly as shown:
                </label>
                <input
                  id="sellerConfirmText"
                  type="text"
                  placeholder='Type "DELETE MY ACCOUNT" to confirm permanent deletion'
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && confirmText === "DELETE MY ACCOUNT") {
                      deleteAccountPermanently();
                    }
                  }}
                />
              </div>

              {error && <div className="seller-error-message">{error}</div>}

              <div className="seller-button-group">
                <button
                  className="seller-back-btn"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Go Back
                </button>
                <button
                  className="seller-delete-btn"
                  onClick={deleteAccountPermanently}
                  disabled={loading || confirmText !== "DELETE MY ACCOUNT"}
                >
                  {loading ? "Deleting JMI Seller Account..." : "Permanently Delete My Account"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="seller-success-step">
              <div className="seller-success-icon">✅</div>
              <h3>JMI Seller Account Deletion Successful</h3>
              <p>
                Your JMI Seller account and all associated business data have been scheduled for deletion. 
              </p>
              <div className="seller-deletion-summary">
                <p><strong>What happens next:</strong></p>
                <ul>
                  <li>All your product listings have been removed from the marketplace</li>
                  <li>Your business profile and seller information have been deleted</li>
                  <li>Most personal and business data has been deleted immediately</li>
                  <li>Remaining data in backups will be purged within 30 days</li>
                  <li>You will no longer be able to access your seller account</li>
                </ul>
              </div>
              <p>
                <strong>Thank you for being a JMI Seller. We hope to welcome you back in the future.</strong>
              </p>
              <button
                className="seller-new-request-btn"
                onClick={resetForm}
              >
                Delete Another Account
              </button>
            </div>
          )}
        </section>

        {/* Support Information */}
        <section className="seller-support-section">
          <h3>Need Help with Account Deletion?</h3>
          <p>
            If you encounter any issues during the account deletion process or have questions 
            about your business data, our seller support team is here to help:
          </p>
          {/* <div className="seller-support-contact">
            <img 
              src="/whatsapp.svg" 
              alt="WhatsApp" 
              className="seller-whatsapp-icon"
              onClick={() => window.open("https://wa.me/918917412728", "_blank")}
            />
            <span>+91 89174 12728 (JMI Seller WhatsApp Support)</span>
          </div> */}
          <div className="seller-support-email">
            <p>Email: <a href="mailto:support@jmiseller.com">support@jmiseller.com</a></p>
          </div>
          <div className="seller-support-hours">
            <p><strong>Seller Support Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST</p>
          </div>
        </section>

        <footer className="seller-page-footer">
          <p>© {new Date().getFullYear()} JMI Seller. All rights reserved.</p>
          <p className="seller-footer-note">
            Protecting your business data and privacy is our priority.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default SellerDeleteAccountPlayStore;